import * as React from 'react';
import { cn } from '@/lib/utils';
import { BackButton, BackButtonProps } from './BackButton';

/**
 * AppPageContainer – Global Page Container (MANDATORY)
 *
 * UNIFIED UI SYSTEM ENFORCEMENT:
 * - ALL pages MUST use this as their outermost wrapper
 * - Enforces consistent max-width, padding, and spacing
 * - Integrates BackButton, Title, Subtitle, and Actions
 *
 * Layout Spec:
 * - max-w-[1400px] mx-auto
 * - px-6 py-4
 * - space-y-4 for section spacing
 *
 * Header Structure:
 * - Row 1: BackButton (left) | Title | Actions (right)
 * - Row 2: Subtitle / context
 *
 * Usage:
 *   <AppPageContainer
 *     title="Hospital Management"
 *     subtitle="Manage all registered hospitals"
 *     backTo="/programs/123/workspace"
 *     backLabel="Back to Workspace"
 *     actions={<Button>Add Hospital</Button>}
 *   >
 *     <AppCard>...</AppCard>
 *   </AppPageContainer>
 */

export interface AppPageContainerProps {
  /** Page content */
  children: React.ReactNode;
  /** Page title (text-base font-semibold) */
  title?: string;
  /** Subtitle / context (text-sm text-muted-foreground) */
  subtitle?: string;
  /** BackButton target route */
  backTo?: string;
  /** BackButton label (default: "Back") */
  backLabel?: string;
  /** Custom BackButton props (overrides backTo/backLabel) */
  backButtonProps?: BackButtonProps;
  /** Actions to render on the right side of header */
  actions?: React.ReactNode;
  /** Hide the header entirely */
  hideHeader?: boolean;
  /** Additional CSS classes for container */
  className?: string;
  /** Additional CSS classes for content area */
  contentClassName?: string;
  /** Custom max width (default: 1400px) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';
  /** Disable default padding */
  noPadding?: boolean;
}

const MAX_WIDTH_CLASSES = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-[1280px]',
  xxl: 'max-w-[1400px]',
  full: 'max-w-full',
};

export function AppPageContainer({
  children,
  title,
  subtitle,
  backTo,
  backLabel,
  backButtonProps,
  actions,
  hideHeader = false,
  className,
  contentClassName,
  maxWidth = 'xxl',
  noPadding = false,
}: AppPageContainerProps) {
  const showBackButton = backTo || backButtonProps;
  const showHeader = !hideHeader && (title || showBackButton || actions);

  return (
    <div
      className={cn(
        'w-full mx-auto',
        MAX_WIDTH_CLASSES[maxWidth],
        !noPadding && 'px-4 sm:px-5 lg:px-6 py-4',
        'app-page-container',
        className
      )}
    >
      {showHeader && (
        <AppPageContainerHeader
          title={title}
          subtitle={subtitle}
          backTo={backTo}
          backLabel={backLabel}
          backButtonProps={backButtonProps}
          actions={actions}
        />
      )}
      <div className={cn('space-y-4', contentClassName)}>{children}</div>
    </div>
  );
}

/**
 * AppPageContainerHeader – Internal header component
 *
 * Structure:
 * - Row 1: BackButton (left) + Title + Actions (right)
 * - Row 2: Subtitle
 */
interface AppPageContainerHeaderProps {
  title?: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  backButtonProps?: BackButtonProps;
  actions?: React.ReactNode;
}

function AppPageContainerHeader({
  title,
  subtitle,
  backTo,
  backLabel,
  backButtonProps,
  actions,
}: AppPageContainerHeaderProps) {
  const showBackButton = backTo || backButtonProps;

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: BackButton + Title + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {showBackButton && (
            <BackButton
              to={backTo}
              label={backLabel}
              {...backButtonProps}
            />
          )}
          {title && (
            <h1 className="text-base font-semibold text-foreground truncate">
              {title}
            </h1>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>

      {/* Row 2: Subtitle */}
      {subtitle && (
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

/**
 * AppPageSection – Section within page with consistent spacing
 *
 * Usage:
 *   <AppPageContainer title="...">
 *     <AppPageSection title="Section 1">
 *       <AppCard>...</AppCard>
 *     </AppPageSection>
 *     <AppPageSection title="Section 2">
 *       <AppCard>...</AppCard>
 *     </AppPageSection>
 *   </AppPageContainer>
 */
export interface AppPageSectionProps {
  children: React.ReactNode;
  /** Section title (text-base font-medium) */
  title?: string;
  /** Section description (text-sm text-muted-foreground) */
  description?: string;
  /** Additional CSS classes */
  className?: string;
}

export function AppPageSection({
  children,
  title,
  description,
  className,
}: AppPageSectionProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {(title || description) && (
        <div className="space-y-1">
          {title && (
            <h2 className="text-base font-medium text-foreground">{title}</h2>
          )}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export default AppPageContainer;
